/**
 * Storage Integrity Service Interfaces
 * Provides type-safe interfaces for secure localStorage management
 * 
 * @version 1.0.0
 * @author SCM Security Team
 * @since Sprint 63
 */

/**
 * Configuration interface for StorageIntegrityService
 */
export interface StorageIntegrityConfig {
  /** Secret key for HMAC-SHA256 signature generation */
  secretKey: string;
  
  /** Algorithm to use for hashing (default: 'sha256') */
  algorithm?: 'sha256' | 'sha512';
  
  /** Enable/disable strict mode (default: true) */
  strictMode?: boolean;
  
  /** Enable/disable automatic recovery (default: true) */
  autoRecovery?: boolean;
  
  /** Prefix for localStorage keys (default: 'scm_') */
  keyPrefix?: string;
  
  /** Maximum age for data in milliseconds (default: 24h) */
  maxAge?: number;
  
  /** Debug mode for development (default: false) */
  debug?: boolean;
}

/**
 * Stored data structure with integrity validation
 */
export interface SecureStorageData {
  /** Original data payload */
  data: any;
  
  /** HMAC-SHA256 signature for integrity validation */
  signature: string;
  
  /** Timestamp when data was stored */
  timestamp: number;
  
  /** Version of the storage format */
  version: string;
  
  /** Optional metadata */
  metadata?: {
    userAgent?: string;
    source?: string;
    [key: string]: any;
  };
}

/**
 * Result of integrity validation
 */
export interface IntegrityValidationResult {
  /** Whether the data is valid */
  isValid: boolean;
  
  /** Reason for invalidity (if any) */
  reason?: IntegrityViolationReason;
  
  /** Additional details about the validation */
  details?: string;
  
  /** Timestamp of validation */
  validatedAt: number;
}

/**
 * Types of integrity violations
 */
export enum IntegrityViolationReason {
  SIGNATURE_MISMATCH = 'signature_mismatch',
  DATA_CORRUPTED = 'data_corrupted',
  EXPIRED = 'expired',
  MISSING_SIGNATURE = 'missing_signature',
  INVALID_FORMAT = 'invalid_format',
  TAMPERING_DETECTED = 'tampering_detected'
}

/**
 * Security event types
 */
export enum SecurityEventType {
  DATA_STORED = 'data_stored',
  DATA_RETRIEVED = 'data_retrieved',
  INTEGRITY_VIOLATION = 'integrity_violation',
  AUTOMATIC_LOGOUT = 'automatic_logout',
  DATA_RECOVERED = 'data_recovered',
  CORRUPTION_DETECTED = 'corruption_detected'
}

/**
 * Security event payload
 */
export interface SecurityEvent {
  /** Type of security event */
  type: SecurityEventType;
  
  /** Timestamp of the event */
  timestamp: number;
  
  /** Storage key involved */
  key: string;
  
  /** Event payload */
  payload?: {
    reason?: IntegrityViolationReason;
    details?: string;
    originalData?: any;
    recoveryAttempted?: boolean;
    [key: string]: any;
  };
}

/**
 * Storage operation options
 */
export interface StorageOptions {
  /** Force operation even in case of warnings */
  force?: boolean;
  
  /** Skip integrity validation */
  skipValidation?: boolean;
  
  /** Custom metadata to store with data */
  metadata?: { [key: string]: any };
  
  /** Custom expiration time (overrides config) */
  maxAge?: number;
}

/**
 * Recovery strategy options
 */
export interface RecoveryOptions {
  /** Whether to attempt recovery */
  attemptRecovery?: boolean;
  
  /** Clear corrupted data if recovery fails */
  clearOnFailure?: boolean;
  
  /** Notify user about recovery attempt */
  notifyUser?: boolean;
  
  /** Trigger logout if recovery fails */
  logoutOnFailure?: boolean;
}

/**
 * Storage statistics and health information
 */
export interface StorageHealth {
  /** Total number of stored items */
  totalItems: number;
  
  /** Number of valid items */
  validItems: number;
  
  /** Number of corrupted items */
  corruptedItems: number;
  
  /** Number of expired items */
  expiredItems: number;
  
  /** Total storage size in bytes */
  storageSize: number;
  
  /** Last integrity check timestamp */
  lastIntegrityCheck: number;
  
  /** Storage implementation health */
  storageAvailable: boolean;
}

/**
 * Callback function type for security events
 */
export type SecurityEventCallback = (event: SecurityEvent) => void;

/**
 * Logger interface for the service
 */
export interface StorageLogger {
  debug(message: string, ...args: any[]): void;
  info(message: string, ...args: any[]): void;
  warn(message: string, ...args: any[]): void;
  error(message: string, ...args: any[]): void;
}
